import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Repeat, CreditCard, Bot, ShieldCheck, Gauge } from 'lucide-react';
import PageTransition from '../components/PageTransition';

const Features = () => {
  const features = [
    {
      icon: MessageSquare,
      title: 'Encrypted Messaging',
      desc: 'Send messages and payments in one secure conversation with end-to-end encryption.',
      points: ['Military-grade encryption', 'Integrated payments', 'Group chats'],
    },
    {
      icon: Repeat,
      title: 'Multi-Chain Support',
      desc: 'Manage assets across multiple blockchains in one unified wallet.',
      points: ['Solana native', 'Cross-chain swaps', 'NFT support'],
    },
    {
      icon: CreditCard,
      title: 'Dobix Pay',
      desc: 'Pay anywhere with crypto to fiat conversion in 150+ countries.',
      points: ['Global coverage', 'Instant conversion', 'Low fees'],
    },
    {
      icon: Bot,
      title: 'Trading Bot',
      desc: 'Trade directly from Telegram with our intelligent trading bot.',
      points: ['Telegram integration', 'Smart orders', 'Auto trading'],
    },
    {
      icon: ShieldCheck,
      title: 'Bank-Level Security',
      desc: 'Your security is our top priority with multiple layers of protection.',
      points: ['Self-custody', 'Biometric auth', 'Regular audits'],
    },
    {
      icon: Gauge,
      title: 'Lightning Fast',
      desc: 'Built on Solana for unmatched transaction speeds.',
      points: ['Sub-second finality', 'High throughput', 'Minimal fees'],
    },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen text-white bg-[#0A0A0A] pt-20">

        {/* Hero */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF6B00]/5 via-transparent to-transparent" />

          <div className="relative max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mb-8"
            >
              <img
                src="/dobix-logo.png"
                alt="Dobix"
                className="h-20 w-20 mx-auto"
              />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-6xl font-bold mb-6 font-heading"
            >
              The Dobix <span className="text-gradient">Ecosystem</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-gray-400 max-w-3xl mx-auto"
            >
              A complete suite of products designed to make crypto accessible to everyone.
            </motion.p>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-dark card-hover rounded-2xl p-8"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B00] to-[#FFB800] rounded-xl flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 mb-4">{feature.desc}</p>
                  <ul className="space-y-2">
                    {feature.points.map((point, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-dark rounded-3xl p-12 text-center relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/10 via-transparent to-[#FFB800]/10" />
              <div className="relative">
                <h2 className="text-4xl font-bold mb-4 font-heading">
                  Ready to Explore?
                </h2>
                <p className="text-gray-400 mb-8">
                  Connect your wallet and start using the Dobix ecosystem today.
                </p>
                <a href="/wallet" className="btn-primary inline-block">
                  Get Started
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Features;